import { type Specialty } from '@/hooks/query/useQuerySpecialties';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

export const OTHER_SPECIALTY_ID = 'other';
export const OTHER_SPECIALTY_NAME = 'Other';

/**
 * Show the selected specialty in the active language: resolve it by id from the
 * (locale-reactive) list instead of the name captured at selection time — the
 * stored `name` is the canonical English identity, not a display string.
 */
export function useSelectedSpecialtyDisplayName(
  selectedSpecialty: { id: string; name: string } | undefined,
  specialties: Specialty[] | undefined
): string | undefined {
  const intl = useIntl();

  return useMemo(() => {
    if (!selectedSpecialty) return undefined;
    if (selectedSpecialty.id === OTHER_SPECIALTY_ID) {
      // The canonical "Other" sentinel is localized for display; anything else
      // in the name slot is free text the clinician typed — never translatable.
      return selectedSpecialty.name === OTHER_SPECIALTY_NAME
        ? intl.formatMessage({
            defaultMessage: 'Other',
            id: 'mZqRPrqizE',
            description:
              'Label shown for the free-text “Other” choice in the clinician specialty selector',
          })
        : selectedSpecialty.name;
    }
    return (
      specialties?.find((s) => s.id === selectedSpecialty.id)?.localizedName ??
      selectedSpecialty.name
    );
  }, [selectedSpecialty, specialties, intl]);
}
